<template>
  <v-card class="donation-flow" :class="compact && 'elevation-0'">
    <v-card-title class="text-center headline mx-auto" :class="compact && 'pl-0 pt-0'">
        <h4>{{ t('splash.title') }}</h4>
    </v-card-title>
    <v-card-text :class="compact && 'pa-0'">
      <p>{{ t('splash.description1') }}</p>
      <p class="mt-2">
        {{ t('splash.description2') }}
      </p>
      <p class="mt-2">
        {{ t('splash.description3') }}
        <a 
          :href="String(t('splash.description3Linkurl'))" 
          target="_blank"
          @click="handleLinkClick"
        >{{
          t('splash.description3LinkText')
        }}</a>
      </p>
    </v-card-text>
    <donation-flow-buttons
      :show-previous="showPreviousButton"
      previous-data-cy="information-view-previous-btn"
      next-data-cy="information-view-next-btn"
      @previous-step="handlePrevious"
      @next-step="handleNext"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent, computed, watch } from 'vue';
import { useLabel } from '@/composables/useLabel';
import store from '@/store';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import { Donation } from '@/interfaces/donation/donation';
import { RequestSession } from '@/interfaces/requestSession';
import { isMemoryGift } from '@/classes/DonationType';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import matomo from '@/services/matomoService';

import DonationFlowButtons from '@/components/DonationFlowButtons.vue';

export default defineComponent({
  components: {
    DonationFlowButtons,
  },
  props: {
    showPreviousButton: Boolean,
    requestHasExternalAgent: Boolean,
    requestHasOrganizationGroup: Boolean,
    compact: Boolean,
  },
  emits: ['addStep', 'removeStep', 'previousStep', 'nextStep'],
  setup(props, { emit }) {
    // using top-level store import instead of instance proxy

    const steps = computed(() => {
      return getDonationStepFlowVersion() == DonationGuideVersion.V1 ? stepV1 : stepV2;
    });

    const donation = computed(() => store.state.donation as Donation);

    const requestSession = computed(() => store.state.requestSession as RequestSession);

    const currentStep = computed(() => store.state.currentStep);

    // Inlined StepperFlowMixin method
    const getDonationStepFlowVersion = () => {
      return store.state.donationGuideVersion || DonationGuideVersion.V1;
    };

    const handleStepRemoval = (oldVal) => {
      emit('removeStep', steps.value.INFORMATION);
      if (oldVal === steps.value.INFORMATION + 1) {
        emit('previousStep');
      } else {
        emit('nextStep');
      }
    };

    watch(
      currentStep,
      (newVal, oldVal) => {
        // Ensure donation and donationType are initialized
        if (!donation.value?.donationType?.type) {
          return;
        }

        if (
          newVal === steps.value.INFORMATION &&
          (!isMemoryGift(donation.value.donationType.type) || props.requestHasExternalAgent)
        ) {
          handleStepRemoval(oldVal);
        } else {
          emit('addStep', steps.value.INFORMATION);
        }
      },
      { immediate: true }
    );

    // Re-evaluate step inclusion when donation type changes
    watch(
      () => donation.value?.donationType,
      () => {
        // Safety check: ensure donation type exists
        if (!donation.value?.donationType) {
          return;
        }
        matomo.trackEvent('Donation Commemorative', 'Viewed', 'Before You Continue Screen');

        const currentVal = currentStep.value;
        if (currentVal === steps.value.INFORMATION) {
          if (props.requestHasOrganizationGroup) {
            emit('removeStep', steps.value.INFORMATION);
          } else {
            emit('addStep', steps.value.INFORMATION);
          }
        }
      }
    );

    const { t } = useLabel();

    const handleLinkClick = () => {
      matomo.trackEvent('Donation Commemorative', 'Clicked', 'Link: Track Gift');
    };
    const handlePrevious = () => {
      matomo.trackEvent('Donation Commemorative', 'Clicked', 'Previous');
      emit('previousStep');
    };

    const handleNext = () => {
      matomo.trackEvent('Donation Commemorative', 'Clicked', 'Next');
      emit('nextStep');
    };

    return {
      t,
      steps,
      donation,
      requestSession,
      currentStep,
      handleLinkClick,
      handlePrevious,
      handleNext,
    };
  },
});
</script>
